<template>
  <div>
    <div class="flex flex-wrap gap-x-lg gap-x-xs-xs-only gap-y-sm align-items-end">
      <div class="form-group m-b-none">
        <label class="font-normal">Viewing</label>
        <select v-model="range" id="payment-chart-range" class="form-control" name="range">
          <option value="today">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="4w">Last 4 weeks</option>
          <option value="3m">Last 3 months</option>
          <option value="12m">Last 12 months</option>
          <option value="year to date">Year to date</option>
          <option value="custom">Custom date range</option>
        </select>
      </div>
      <div v-if="range === 'custom'" class="flex gap-x-sm">
        <div class="form-group m-b-none">
          <label class="font-normal">From</label>
          <input type="date" v-model="customStartDate" class="form-control" />
        </div>
        <div class="form-group m-b-none">
          <label class="font-normal">To</label>
          <input type="date" v-model="customEndDate" class="form-control" />
        </div>
        <div v-if="error" class="form-group m-b-none">
          <p class="text-red-500">{{ errorMessage }}</p>
        </div>
      </div>
      <div class="form-group m-b-none">
        <label class="font-normal">{{ range === 'custom' ? 'Group data by' : 'Grouped' }}</label>
        <select v-model="interval" id="payment-interval" class="form-control" name="interval">
          <option v-for="i in availableIntervals" :value="i">{{ capitalize(i) }}</option>
        </select>
      </div>
      <div class="form-group m-b-none flex flex-col align-items-center" v-if="range === 'custom'">
          <button @click="applyCustomDateRange" class="btn btn-primary">Apply</button>
        </div>
      <div v-if="range !== 'custom'" class="form-group m-b-none">
        <label class="font-normal">Compared to</label>
        <select id="payment-comparison" class="form-control" name="comparison">
          <option value="previous-period">Previous period</option>
        </select>
      </div>
    </div>
    <div class="relative grid grid-cols-3 gap-24 sm-grid-cols-1 md-grid-cols-1 border-t border-gray-200 border-solid m-t-lg p-t-md">
      <div>
        <h2 class="font-heavy m-t-sm">Charge Metrics</h2>
        <p>Basic revenue metrics for your team. Keep scrolling for the payment feed<span v-if="showRecurring"> and recurring subscription metrics</span>.</p>
      </div>
      <div class="grid grid-cols-2 md-grid-cols-1 sm-grid-cols-1 gap-12 col-span-2">
        <div class="col-span-2 md-col-span-1 sm-col-span-1">
          <metrics-chart v-if="shouldShowChart('category-breakdown-net')" :key="keyFor('category-breakdown-net')" chart-key="category-breakdown-net" :endpoint="this.categoryBreakdownNetUrl"></metrics-chart>
        </div>
        <div>
          <metrics-chart v-if="shouldShowChart('gross')" :key="keyFor('gross')" chart-key="gross" :endpoint="this.grossUrl"></metrics-chart>
        </div>
        <div>
          <metrics-chart v-if="shouldShowChart('net')" :key="keyFor('net')" chart-key="net" :endpoint="this.netUrl"></metrics-chart>
        </div>
        <div>
          <metrics-chart v-if="shouldShowChart('charge-count')" :key="keyFor('charge-count')" chart-key="charge-count" :endpoint="this.chargeCountUrl"></metrics-chart>
        </div>
        <div>
          <metrics-chart v-if="shouldShowChart('charge-avg')" :key="keyFor('charge-avg')" chart-key="charge-avg" :endpoint="this.chargeAvgUrl"></metrics-chart>
        </div>
      </div>
    </div>
    <div v-if="showRecurring" class="relative grid grid-cols-3 gap-24 sm-grid-cols-1 md-grid-cols-1 border-t border-gray-200 border-solid m-t-lg p-t-md">
      <div>
        <h2 class="font-heavy m-t-sm">Recurring Subscription Metrics</h2> 
        <p>Understand your recurring subscription business model, including new subscriptions and cancellations.</p>
      </div>
      <div class="grid grid-cols-2 md-grid-cols-1 sm-grid-cols-1 gap-12 col-span-2">
        <div class="relative">
          <metrics-chart v-if="shouldShowChart('new-subscriptions')" :key="keyFor('new-subscriptions')" chart-key="new-subscriptions" :endpoint="this.newSubscriptionsUrl"></metrics-chart>
          <div class="-m-t-sm z-10 relative">
            <a @click.prevent="openFeed('BillingSubscriptionEvent', 'event_name_eq', 'subscription.created')" class="underline font-heavy text-blue-700 flex align-items-center justify-center gap-sm">
              <span>View new subscriptions {{ humanizedRange }}</span> <i class="fa fa-angle-right"></i>
            </a>
          </div>
        </div>
        <div>
          <metrics-chart v-if="shouldShowChart('cancelled-subscriptions')" :key="keyFor('cancelled-subscriptions')" chart-key="cancelled-subscriptions" :endpoint="this.canceledSubscriptionsUrl"></metrics-chart>
          <div class="-m-t-sm z-10 relative">
            <a @click.prevent="openFeed('BillingSubscriptionEvent', 'event_name_eq', 'subscription.canceled')" class="underline font-heavy text-blue-700 flex align-items-center justify-center gap-sm">
              <span>View cancelled subscriptions {{ humanizedRange }}</span> <i class="fa fa-angle-right"></i>
            </a>
          </div>
        </div>
        <div v-if="showRenewed">
          <metrics-chart v-if="shouldShowChart('renewed-subscriptions')" :key="keyFor('renewed-subscriptions')" chart-key="renewed-subscriptions" :endpoint="this.renewedSubscriptionsUrl"></metrics-chart>
          <div class="-m-t-sm z-10 relative">
            <a @click.prevent="openFeed('BillingSubscriptionEvent', 'event_name_eq', 'subscription.renewed')" class="underline font-heavy text-blue-700 flex align-items-center justify-center gap-sm">
              <span>View renewed subscriptions {{ humanizedRange }}</span> <i class="fa fa-angle-right"></i>
            </a>
          </div>
        </div>
        <div>
          <metrics-chart v-if="shouldShowChart('active-subscribers')" :key="keyFor('active-subscribers')" chart-key="active-subscribers" :endpoint="this.activeSubscribersUrl"></metrics-chart>
        </div>
        <div>
          <metrics-chart v-if="shouldShowChart('mrr')" :key="keyFor('mrr')" chart-key="mrr" :endpoint="this.mrrUrl"></metrics-chart>
        </div>
        <div>
          <metrics-chart v-if="shouldShowChart('rev-per-sub')" :key="keyFor('rev-per-sub')" chart-key="rev-per-sub" :endpoint="this.revSubscriberUrl"></metrics-chart>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import MetricsChart from './metrics_chart.vue';

export default {
  name: "payment-charts",
  components: { MetricsChart },
  props: {
    showRecurring: {
      type: Boolean,
      default: false,
    },
    showRenewed: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      range: "7d",
      interval: "daily",
      currentlyExploring: null,
      // This is the model tied to the input fields
      customStartDate: this.getDefaultStartDate(),
      customEndDate: this.getDefaultEndDate(),
      // This is the effective values for the API calls, after the user clicks apply
      effectiveCustomStartDate: this.getDefaultStartDate(),
      effectiveCustomEndDate: this.getDefaultEndDate(),
      error: null,
      errorMessage: "",
      // Debugging settings if you only want to inspect certain charts. Helps make logging cleaner.
      debug: {
        enabled: false,
        chartsEnabled: ['category-breakdown-net', 'net']
      }
    }
  },
  computed: {
    customStartDateForApi() {
      if (this.range === 'custom') {
        return this.effectiveCustomStartDate;
      }
      return null;
    },
    customEndDateForApi() {
      if (this.range === 'custom') {
        return this.effectiveCustomEndDate;
      }
      return null;
    },
    categoryBreakdownNetUrl() {
      return this.$apiService.categoryBreakdownNet(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi);
    },
    categoryBreakdownCountUrl() {
      return this.$apiService.categoryBreakdownCount(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi);
    },
    grossUrl() {
      return this.$apiService.grossVolume(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi);
    },
    netUrl() {
      return this.$apiService.netVolume(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi);
    },
    activeSubscribersUrl() {
      return this.$apiService.activeSubscribers(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi);
    },
    mrrUrl() {
      return this.$apiService.mrr(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi);
    },
    newSubscribersUrl() {
      // Deprecated
      return this.$apiService.newSubscribers(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi);
    },
    newSubscriptionsUrl() {
      return this.$apiService.newSubscriptions(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi);
    },
    canceledSubscriptionsUrl() {
      return this.$apiService.canceledSubscriptions(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi);
    },
    renewedSubscriptionsUrl() {
      return this.$apiService.renewedSubscriptions(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi);
    },
    revSubscriberUrl() {
      return this.$apiService.revPerSubscriber(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi);
    },
    chargeCountUrl() {
      return this.$apiService.chargeCount(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi);
    },
    chargeAvgUrl() {
      return this.$apiService.chargeAvg(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi);
    },
    availableIntervals() {
      if (this.range === 'today') {
        return ["hourly"]
      } else if (this.range === "7d") {
        return ["daily"]
      } else if (this.range === "4w") {
        return ["daily", "weekly"]
      } else if (this.range === "custom") {
        return ["daily"];
      } else {
        return ["monthly"]
      }
    },
    humanizedRange() {
      if (this.range === 'today') {
        return 'today';
      } else if (this.range === '7d') {
        return 'in the last 7 days';
      } else if (this.range === '4w') {
        return 'in the last 4 weeks';
      } else if (this.range === '3m') {
        return 'in the last 3 months';
      } else if (this.range === '12m') {
        return 'in the last 12 months';
      } else if (this.range === 'year to date') {
        return 'in the year to date';
      } else if (this.range === 'custom') {
        return `from ${this.formatDate(this.effectiveCustomStartDate)} to ${this.formatDate(this.effectiveCustomEndDate)}`;
      }
    }
  },
  watch: {
    range(newRange) {
      if (newRange === 'today') {
        this.interval = "hourly"
      } else if (newRange === "7d") {
        this.interval = "daily"
      } else if (newRange === "4w") {
        this.interval = "weekly"
      } else if (newRange === '3m' || newRange === '12m' || newRange === 'year to date') {
        this.interval = "monthly"
      } else if (newRange === 'custom') {
        this.interval = "daily";
      }

      this.$notificationManager.$emit('chart-range-changed', newRange, this.interval);
    }
  },
  methods: {
    shouldShowChart(chartName) {
      if (!this.debug.enabled) return true;
      return this.debug.chartsEnabled.includes(chartName);
    },
    keyFor(chartName) {
      if (this.range === 'custom') {
        return `${chartName}-custom-${this.effectiveCustomStartDate}-${this.effectiveCustomEndDate}-${this.interval}`;
      }
      return `${chartName}-${this.range}-${this.interval}`;
    },
    capitalize(name) {
      return _.capitalize(name);
    },
    getDefaultStartDate() {
      return moment().subtract(30, 'days').format('YYYY-MM-DD');
    },
    getDefaultEndDate() {
      return moment().format('YYYY-MM-DD');
    },
    formatDate(dateString) {
      return moment(dateString).format('MMM D, YYYY');
    },
    getDaysDifference() {
      if (!this.customStartDate || !this.customEndDate) return 30;
      return moment(this.customEndDate).diff(moment(this.customStartDate), 'days');
    },
    updateCustomDateRange() {
      if (!this.customStartDate || !this.customEndDate) return;
      
      // Ensure dates are in YYYY-MM-DD format
      this.validateDateFormat();
      
      if (moment(this.customEndDate).isBefore(moment(this.customStartDate))) {
        this.customEndDate = this.customStartDate;
      }
      
      this.interval = "daily";
    },
    validateDateFormat() {
      // Ensure dates are in YYYY-MM-DD format
      try {
        // Check if dates are valid
        const startDate = moment(this.customStartDate);
        const endDate = moment(this.customEndDate);
        
        if (!startDate.isValid()) {
          this.error = true;
          this.errorMessage = "Invalid start date, resetting to default";
          this.customStartDate = this.getDefaultStartDate();
        }
        
        if (!endDate.isValid()) {
          this.error = true;
          this.errorMessage = "Invalid end date, resetting to default";
          this.customEndDate = this.getDefaultEndDate();
        }
        
        // Ensure format is YYYY-MM-DD
        this.customStartDate = startDate.format('YYYY-MM-DD');
        this.customEndDate = endDate.format('YYYY-MM-DD');
      } catch (e) {
        this.error = true;
        this.errorMessage = "Date validation error, resetting to defaults";
        // Reset to defaults if there's an error
        this.customStartDate = this.getDefaultStartDate();
        this.customEndDate = this.getDefaultEndDate();
      }
    },
    applyCustomDateRange() {
      this.updateCustomDateRange();
      
      // Store the values explicitly to ensure they're properly captured
      const startDate = this.customStartDate;
      const endDate = this.customEndDate;
      
      // Set the effective dates that will be used for API calls
      this.effectiveCustomStartDate = startDate;
      this.effectiveCustomEndDate = endDate;
    },
    openFeed(objectType, primaryPredicateKey, primaryPredicateValue) {
      let createdAtStart, createdAtEnd;
      
      if (this.range === 'custom') {
        createdAtStart = moment(this.effectiveCustomStartDate).startOf('day');
        createdAtEnd = moment(this.effectiveCustomEndDate).endOf('day');
      } else {
        createdAtStart = this.range === 'today' ? moment().startOf('day') :
                          this.range === '7d' ? moment().subtract(7, 'days').startOf('day') :
                          this.range === '4w' ? moment().subtract(4, 'weeks').startOf('day') :
                          this.range === '3m' ? moment().subtract(3, 'months').startOf('day') :
                          this.range === '12m' ? moment().subtract(12, 'months').startOf('day') :
                          moment().startOf('year');
        createdAtEnd = moment().endOf('day');
      }

      this.currentlyExploring = {
        objectType: objectType,
        primaryPredicateKey: primaryPredicateKey,
        primaryPredicateValue: primaryPredicateValue,
        createdAtStart: createdAtStart,
        createdAtEnd: createdAtEnd,
      }
      this.$notificationManager.$emit('open-feed', this.currentlyExploring);
    }
  },
};
</script>
